import { Link } from 'react-router-dom';
import { MachineTranslationNote } from '../components/MachineTranslationNote';
import { ArrowLeft } from 'lucide-react';
import { LogoWordmark } from '../components/Logo';
import { allLegalDocs } from '../legal';
import { formatDate } from '../lib/format';
import { useT } from '../i18n';

export default function LegalPage({ slug }: { slug: string }) {
  const t = useT();
  const doc = allLegalDocs.find((d) => d.slug === slug);
  if (!doc) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <p className="font-display text-xl font-bold">{t('Document not found')}</p>
        <Link to="/" className="btn-secondary mt-4">
          {t('Back home')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-surface dark:bg-surface-dark">
      <header className="border-b border-slate-200 dark:border-slate-800">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" aria-label={t('Sansara home')}>
            <LogoWordmark />
          </Link>
          <Link to="/" className="btn-ghost text-sm">
            <ArrowLeft size={16} />
            {t('Home')}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {/* Above the title on purpose: in any other language the document below
            is a MACHINE translation (founder decision 2026-10-19), and the reader
            has to learn that — and that the English governs — BEFORE reading it,
            not after. Renders nothing for English. */}
        <MachineTranslationNote className="mb-6" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t(doc.title)}</h1>
        <p className="mt-3 text-sm text-ink-faint dark:text-slate-400">
          {t('Effective {effective} · Last updated {updated}', {
            effective: formatDate(doc.effectiveDate),
            updated: formatDate(doc.lastUpdated),
          })}
        </p>
        {doc.intro ? (
          <p className="mt-6 text-base leading-relaxed text-ink-soft dark:text-slate-400">{t(doc.intro)}</p>
        ) : null}

        <div className="mt-10 space-y-10">
          {doc.sections.map((section, i) => (
            <section key={i}>
              <h2 className="font-display text-xl font-bold tracking-tight">
                {i + 1}. {t(section.heading)}
              </h2>
              <div className="mt-3 space-y-3">
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="text-[15px] leading-relaxed text-ink-soft dark:text-slate-400">
                    {t(p)}
                  </p>
                ))}
              </div>
              {section.bullets?.length ? (
                <ul className="mt-3 space-y-2">
                  {section.bullets.map((b, j) => (
                    <li key={j} className="flex gap-2.5 text-[15px] leading-relaxed text-ink-soft dark:text-slate-400">
                      <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                      {t(b)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>

        <nav className="mt-16 border-t border-slate-200 pt-8 dark:border-slate-800" aria-label={t('Other policies')}>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint dark:text-slate-400">
            {t('All policies')}
          </p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            {allLegalDocs.map((d) => (
              <Link
                key={d.slug}
                to={`/${d.slug}`}
                className={`text-sm ${
                  d.slug === slug
                    ? 'font-bold text-brand-700 dark:text-brand-400'
                    : 'text-ink-soft hover:text-brand-700 dark:text-slate-400 dark:hover:text-brand-400'
                }`}
              >
                {t(d.title)}
              </Link>
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
}
